'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, ArrowUpRight } from 'lucide-react'
import type { Team, Media } from '@/payload-types'
import { RichText } from '../RichText'
import { getMediaUrl } from '../../lib/media'
import Link from 'next/link'

interface AboutUsClientProps {
  heading: any
  detail?: any
  teamMembers: Team[]
}

export function AboutUsClient({ heading, detail, teamMembers }: AboutUsClientProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [direction, setDirection] = useState(0)

  const handlePrevious = () => {
    setDirection(-1)
    setCurrentIndex((prev) => (prev === 0 ? teamMembers.length - 1 : prev - 1))
  }

  const handleNext = () => {
    setDirection(1)
    setCurrentIndex((prev) => (prev === teamMembers.length - 1 ? 0 : prev + 1))
  }

  const currentMember = teamMembers[currentIndex]
  const photo = currentMember.photo as Media | null | undefined
  const photoUrl = getMediaUrl(photo, 'card')

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 20 : -20,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -20 : 20,
      opacity: 0,
    }),
  }

  return (
    <section className="py-12 md:py-24 px-6 md:px-12">
      <div className="max-w-380 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_5fr] gap-4 md:gap-12 lg:gap-12 items-start lg:items-center">
          {/* Left side - Team member photo and info */}
          <div className="space-y-4 md:space-y-6 order-2 lg:order-1">
            {photoUrl && (
              <div className="relative aspect-[4/5] w-full max-w-xs md:max-w-sm mx-auto lg:mx-0 overflow-hidden rounded-lg">
                <Image
                  src={photoUrl}
                  alt={currentMember.name || ''}
                  fill
                  className="object-cover"
                />
              </div>
            )}

            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                }}
              >
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                  {currentMember.name}
                </h3>
                <p className="text-gray-600 mt-1 text-sm md:text-base">{currentMember.title}</p>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-center lg:justify-start gap-3 md:gap-4 pt-2 md:pt-4">
              <button
                onClick={handlePrevious}
                className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-primary/30 flex items-center justify-center hover:border-primary/60 transition-colors"
                aria-label="Previous team member"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={handleNext}
                className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-primary/30 flex items-center justify-center hover:border-primary/60 transition-colors"
                aria-label="Next team member"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>

              {/* Dot indicators */}
              <div className="flex gap-1.5 md:gap-2 ml-3 md:ml-4">
                {teamMembers.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setDirection(index > currentIndex ? 1 : -1)
                      setCurrentIndex(index)
                    }}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentIndex ? 'bg-primary' : 'bg-primary/30'
                    }`}
                    aria-label={`Go to team member ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right side - Content */}
          <div className="space-y-4 md:space-y-6 mb-8 lg:mb-0 order-1 lg:order-2">
            {heading && (
              <RichText
                data={heading}
                className="text-3xl md:text-4xl lg:text-6xl font-medium leading-tight text-gray-900 [&_span.font-serif]:text-brand-gold [&_span.font-serif]:italic"
              />
            )}

            {detail && (
              <RichText
                data={detail}
                className="text-base md:text-xl [&_p]:text-gray-600 [&_p]:leading-relaxed [&_p]:mb-4"
              />
            )}

            <div className="pt-2 md:pt-4">
              <Link
                href="/about"
                className="inline-flex items-center gap-2 px-6 md:px-8 py-2.5 md:py-3 text-gray-900 rounded-full border border-primary/50 hover:border-primary transition-colors backdrop-blur-sm font-medium text-sm md:text-base"
              >
                Your Team
                <ArrowUpRight className="w-4 md:w-5 h-4 md:h-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
